<template>
  <div class="activity-navigator" :class="cssClasses">
    <div class="label-page">Page</div>
    <div ref="navigatorEl" class="scroll-wrapper" @scroll="updateScrollMetrics">
      <!-- Pages button -->
      <activity-navigator-button
        v-for="(page, index) in totalPages"
        :key="index + 1"
        :active="isPageActive(index + 1)"
        :selected="isPageSelected(index + 1)"
        :disabled="disabled"
        class="page-button"
        @click="handleNavigation(index + 1)"
      >
        {{ index + 1 }}
        <template #overflow-menu>
          <slot name="overflow-menu"></slot>
        </template>
      </activity-navigator-button>

      <!-- Add button -->
      <activity-navigator-button
        v-if="showAddButton"
        :icon-only="true"
        :disabled="disabled || isAtPageLimit || hasReachedMaximumNoOfComponents"
        kind="primary"
        class="btn-add-page"
        @click="handleAdd"
      >
        <cv-interactive-tooltip
          v-if="isAtPageLimit"
          direction="bottom"
          class="field-tooltip"
          @click.stop
        >
          <template #trigger>
            <svg-icon name="Plus16" kind="action" />
          </template>
          <template #content>
            {{ t("component_add_page_limit") }}
          </template>
        </cv-interactive-tooltip>
        <svg-icon v-else name="Plus16" kind="action" />
      </activity-navigator-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import ActivityNavigatorButton from "@plugins/activity-navigator/components/ActivityNavigatorButton.vue";
import { NAMESPACE, VALIDATION } from "@modules/editor/service/editor.constant";
import { computed, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useScrollNearEnd } from "@utils/scroll.utils";

// Props
interface Props {
  totalPages?: number;
  activePageNo?: number;
  showAddButton?: boolean;
  disabled?: boolean;
  isEditingMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  totalPages: 0,
  activePageNo: 0,
  showAddButton: false,
  disabled: false,
  isEditingMode: false,
});

const emit = defineEmits<{
  navigate: [data: unknown];
  addPage: [];
}>();

// Scroll tracking
const navigatorEl = useTemplateRef<HTMLDivElement>("navigatorEl");
const { hasOverflow, isScrolledNearEnd, updateScrollMetrics } = useScrollNearEnd(navigatorEl, {
  axis: "horizontal",
});

// Composables
const { t } = useI18n();
const store = useStore();
const currentPageId = computed(() => store.getters[`${NAMESPACE.LESSON}/currentPageId`]);
const getLeafComponentsInActivity = computed(
  () => store.getters[`${NAMESPACE.LESSON}/getLeafComponentsInActivity`],
);

// Computed
const getTotalPages = computed(() => {
  return props.totalPages;
});

const isAtPageLimit = computed(() => {
  return getTotalPages.value >= VALIDATION.MAX_PAGES;
});

const hasReachedMaximumNoOfComponents = computed(() => {
  return getLeafComponentsInActivity.value(currentPageId.value).length >= VALIDATION.MAX_COMPONENTS;
});

const cssClasses = computed(() => ({
  "activity-navigator-overflow": hasOverflow.value,
  "activity-navigator-end": isScrolledNearEnd.value,
}));

// Methods
const handleNavigation = (value) => {
  emit("navigate", value);
};

const handleAdd = () => {
  emit("addPage");
};

const isPageActive = (index) => {
  if (!props.isEditingMode) return false;
  return index === props.activePageNo;
};

const isPageSelected = (page) => {
  return page === props.activePageNo;
};
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/abstracts/variables" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.activity-navigator {
  position: relative;
  display: flex;
  gap: $spacing-03;
  width: 100%;
  margin: 0 auto;
  z-index: z-map("default");

  &.activity-navigator-overflow {
    &::after {
      @include position-mixin($width: 40px, $height: 100%, $top: 0, $right: 0, $pseudo: true);
      background: $sticky-navigator-gradient;
      transition: opacity 0.3s ease-in-out;
      opacity: 1;
      pointer-events: none;
    }
  }

  &.activity-navigator-end {
    &::after {
      opacity: 0;
    }
  }

  .label-page {
    @include carbon--type-style("label-01");
    padding-top: $spacing-05;
    color: $text-02;
    white-space: nowrap;
  }

  .scroll-wrapper {
    display: flex;
    padding: $spacing-03 0;
    overflow-x: auto;
  }
}
</style>
